/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  parseTerminadoPdf, parseTerminadoText, createTerminadoBatch,
  listTerminadoBatches, getTerminadoBatch, deleteTerminadoBatch,
  updateTerminadoName, listTerminadoPending,
  applyTerminadoBatchToCrm, getSyncJobStatus,
  forceApplyTerminadoName, ignoreTerminadoName, unignoreTerminadoName,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  CheckCircle2, Clock, AlertTriangle, Upload, FileText, Loader2,
  Trash2, ChevronRight, ChevronDown, Mail, ClipboardPaste, Send,
  Ban, Undo2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  invoiced:    { label: "Ya facturado",  icon: CheckCircle2, color: "emerald" },
  pending:     { label: "Esperando",     icon: Clock,        color: "amber" },
  applied:     { label: "Aplicado CRM",  icon: CheckCircle2, color: "slate" },
  discrepancy: { label: "Discrepancia",  icon: AlertTriangle, color: "rose" },
  ignored:     { label: "Ignorado",      icon: Ban,          color: "slate" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  const colorClasses = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    slate:   "bg-slate-100 text-slate-700 border-slate-300",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
  }[m.color];
  return (
    <span
      data-testid={`status-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-md border ${colorClasses}`}
    >
      <Icon size={12} />
      {m.label}
    </span>
  );
}

export default function CerradoTerminado() {
  const [batches, setBatches] = useState([]);
  const [pending, setPending] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);

  // Upload/paste state
  const fileRef = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  // Preview state (after parsing)
  const [preview, setPreview] = useState(null); // { date, raw_text, names, count }
  const [saving, setSaving] = useState(false);

  // Expanded batches & batch detail cache
  const [expanded, setExpanded] = useState({});
  const [batchDetails, setBatchDetails] = useState({});
  const [toDelete, setToDelete] = useState(null);
  // Apply-to-CRM job state
  const [applyJob, setApplyJob] = useState(null); // { batchId, jobId, status, done, total, log, results }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([
        listTerminadoBatches(),
        listTerminadoPending(),
      ]);
      setBatches(b);
      setPending(p);
    } catch (e) {
      toast.error("Error al cargar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // --- Upload / paste / parse ---
  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const r = await parseTerminadoPdf(file);
      setPreview(r);
      toast.success(`Detectados ${r.count} nombres`);
    } catch (e) {
      toast.error("Error procesando PDF: " + (e?.response?.data?.detail || e.message));
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handlePasteParse = async () => {
    if (!pasteText.trim()) {
      toast.error("Pega el contenido del email primero");
      return;
    }
    setParsing(true);
    try {
      const r = await parseTerminadoText(pasteText);
      setPreview(r);
      setPasteOpen(false);
      toast.success(`Detectados ${r.count} nombres`);
    } catch (e) {
      toast.error("Error procesando texto: " + (e?.response?.data?.detail || e.message));
    } finally {
      setParsing(false);
    }
  };

  // --- Apply to CRM ---
  const pollJob = async (batchId, jobId) => {
    let last = null;
    for (let i = 0; i < 600; i++) { // up to ~20 min
      try {
        const j = await getSyncJobStatus(jobId);
        last = j;
        setApplyJob({ batchId, jobId, ...j });
        if (j.status === "done" || j.status === "error" || j.status === "cancelled") {
          break;
        }
      } catch (e) {
        // 404 = job lost; stop polling
        if (e?.response?.status === 404) break;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    return last;
  };

  const applyToCrm = useCallback(async (batchId) => {
    try {
      const r = await applyTerminadoBatchToCrm(batchId);
      if (!r.job_id) {
        toast.message(r.message || "Nada que aplicar en el CRM");
        return null;
      }
      toast.success(`Aplicando ${r.total} trips al CRM... (job ${r.job_id})`);
      setApplyJob({ batchId, jobId: r.job_id, status: "queued", total: r.total, done: 0, log: [] });
      const final = await pollJob(batchId, r.job_id);
      if (final?.status === "done") {
        const ok = (final.results || []).filter((x) => x.status === "ok").length;
        const ko = (final.results || []).filter((x) => x.status !== "ok").length;
        toast.success(`CRM actualizado: ${ok} OK · ${ko} con incidencia`);
      } else if (final?.status === "error") {
        toast.error("Error en aplicación CRM: " + (final.error || "desconocido"));
      }
      reload();
      // Refresh expanded batch's detail too
      if (expanded[batchId]) {
        const d = await getTerminadoBatch(batchId);
        setBatchDetails((s) => ({ ...s, [batchId]: d }));
      }
      return final;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error("No se pudo iniciar la aplicación al CRM: " + msg);
      return null;
    }
  }, [expanded, reload]);

  const saveBatch = async () => {
    if (!preview || preview.names.length === 0) return;
    setSaving(true);
    try {
      const r = await createTerminadoBatch({
        source_date: preview.date || null,
        raw_text: preview.raw_text || "",
        names: preview.names,
      });
      const invoicedCount = preview.names.filter((n) => n.status === "invoiced").length;
      toast.success(`Batch guardado · ${r.count} nombres`);
      setPreview(null);
      setPasteText("");
      await reload();
      // Auto-expand the new batch so the user sees actions for discrepancies
      // and any remaining status info without an extra click.
      setExpanded((s) => ({ ...s, [r.id]: true }));
      try {
        const d = await getTerminadoBatch(r.id);
        setBatchDetails((s) => ({ ...s, [r.id]: d }));
      } catch {}
      // Auto-apply to CRM if there are invoiced names
      if (invoicedCount > 0) {
        applyToCrm(r.id);
      }
    } catch (e) {
      toast.error("Error guardando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  // --- Batch expand / detail ---
  const toggleBatch = async (id) => {
    setExpanded((s) => ({ ...s, [id]: !s[id] }));
    if (!batchDetails[id]) {
      try {
        const d = await getTerminadoBatch(id);
        setBatchDetails((s) => ({ ...s, [id]: d }));
      } catch {}
    }
  };

  const handleDeleteBatch = async () => {
    if (!toDelete) return;
    try {
      await deleteTerminadoBatch(toDelete);
      toast.success("Batch eliminado");
      setToDelete(null);
      setBatchDetails((s) => { const n = {...s}; delete n[toDelete]; return n; });
      reload();
    } catch (e) {
      toast.error("No se pudo eliminar: " + (e?.response?.data?.detail || e.message));
    }
  };

  const refreshBatchDetail = useCallback(async (batchId) => {
    const d = await getTerminadoBatch(batchId);
    setBatchDetails((s) => ({ ...s, [batchId]: d }));
  }, []);

  const handleForceApply = useCallback(async (batchId, name) => {
    try {
      const r = await forceApplyTerminadoName(batchId, name.idx);
      toast.success(`Buscando "${name.clean_name}" en el CRM…`);
      setApplyJob({ batchId, jobId: r.job_id, status: "queued", total: 1, done: 0, log: [] });
      const final = await pollJob(batchId, r.job_id);
      if (final?.status === "done") {
        const res = (final.results || [])[0];
        if (res?.status === "ok") {
          toast.success(`"${name.clean_name}" aplicado en el CRM`);
        } else if (res?.status === "not_found") {
          toast.error(`"${name.clean_name}" no se encontró en el CRM`);
        } else {
          toast.error(`"${name.clean_name}": ${res?.error || "error"}`);
        }
      }
      await reload();
      await refreshBatchDetail(batchId);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  }, [reload, refreshBatchDetail]);

  const handleIgnore = useCallback(async (batchId, name) => {
    const reason = window.prompt(
      `¿Por qué ignoras "${name.clean_name}"? (opcional)`,
      "",
    );
    if (reason === null) return;
    try {
      await ignoreTerminadoName(batchId, name.idx, reason || null);
      toast.success(`"${name.clean_name}" ignorado`);
      await reload();
      await refreshBatchDetail(batchId);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  }, [reload, refreshBatchDetail]);

  const handleUnignore = useCallback(async (batchId, name) => {
    try {
      await unignoreTerminadoName(batchId, name.idx);
      toast.success(`"${name.clean_name}" restaurado`);
      await reload();
      await refreshBatchDetail(batchId);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  }, [reload, refreshBatchDetail]);

  const handleMoveToPending = useCallback(async (batchId, name) => {
    try {
      await updateTerminadoName(batchId, name.idx, { status: "pending" });
      toast.success(
        `"${name.clean_name}" movido a Esperando — se auto-aplicará cuando emitas la factura final que lo incluya`
      );
      await reload();
      await refreshBatchDetail(batchId);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  }, [reload, refreshBatchDetail]);

  // --- Counters for preview ---
  const previewCounts = preview ? preview.names.reduce((acc, n) => {
    acc[n.status] = (acc[n.status] || 0) + 1;
    return acc;
  }, {}) : {};

  return (
    <div className="space-y-6" data-testid="cerrado-terminado-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">PANEL</div>
          <h1 className="font-cabinet text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Cerrado → Terminado
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Sube los emails de Susana o pega el listado. La app cruza cada nombre con tus facturas finales:
            si ya está facturado lo marca verde; si no, queda en la lista de espera y se concilia
            automáticamente cuando emitas la factura que lo incluya.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="upload-pdf-input"
          />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={parsing}
            data-testid="upload-pdf-btn"
          >
            {parsing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
            Subir PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => setPasteOpen(true)}
            disabled={parsing}
            data-testid="paste-text-btn"
          >
            <ClipboardPaste size={14} className="mr-2" />
            Pegar texto
          </Button>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Batches procesados"   value={batches.length}                        testid="kpi-batches" />
        <KpiCard label="En espera de factura" value={pending.count}    color="amber"        testid="kpi-pending" />
        <KpiCard label="Ya facturados"        value={batches.reduce((a, b) => a + (b.invoiced || 0), 0)} color="emerald" testid="kpi-invoiced" />
        <KpiCard label="Discrepancias"        value={batches.reduce((a, b) => a + (b.discrepancy || 0), 0)} color="rose" testid="kpi-discrepancy" />
      </div>

      {/* Apply-to-CRM progress banner */}
      {applyJob && (applyJob.status === "running" || applyJob.status === "queued") && (
        <div
          className="flex items-center gap-3 px-5 py-3 bg-slate-900 text-white rounded-lg"
          data-testid="apply-progress-banner"
        >
          <Loader2 size={16} className="animate-spin shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-sm">
              Aplicando Estado=Terminado en el CRM…
            </div>
            <div className="text-xs text-slate-300">
              {applyJob.done}/{applyJob.total} trips procesados
              {applyJob.log?.length > 0 && (
                <span className="ml-2 opacity-70">· {applyJob.log[applyJob.log.length - 1]}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview after parsing */}
      {preview && (
        <section className="bg-white border-2 border-slate-300 rounded-lg overflow-hidden" data-testid="preview-section">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-cabinet text-lg font-bold text-slate-900">
                Vista previa del email
                {preview.date && (
                  <span className="ml-3 text-sm font-normal text-slate-500">· {preview.date}</span>
                )}
              </div>
              <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-3">
                <span><strong>{preview.count}</strong> nombres detectados</span>
                {Object.entries(previewCounts).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1">
                    <StatusBadge status={k} /> × {v}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPreview(null)} data-testid="preview-cancel-btn">
                Descartar
              </Button>
              <Button onClick={saveBatch} disabled={saving} data-testid="preview-save-btn">
                {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                Guardar batch
              </Button>
            </div>
          </div>
          <NamesTable names={preview.names} />
        </section>
      )}

      {/* Waiting list */}
      {pending.count > 0 && (
        <section className="bg-amber-50/40 border border-amber-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-amber-200 flex items-center gap-2">
            <Clock size={16} className="text-amber-700" />
            <div className="font-cabinet text-lg font-bold text-slate-900">
              Lista de espera <span className="text-amber-700">({pending.count})</span>
            </div>
            <div className="text-xs text-slate-500 ml-auto">
              Se mueven solas a "facturado" cuando emitas la factura final que las incluya
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {pending.items.map((p, i) => (
              <div
                key={`${p.batch_id}-${p.idx}-${i}`}
                className="flex items-start gap-2 px-3 py-2 bg-white border border-amber-200 rounded text-sm"
                data-testid={`pending-name-${p.clean_name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Clock size={12} className="text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{p.clean_name}</div>
                  {p.notes?.length > 0 && (
                    <div className="text-[10px] text-slate-500 truncate">{p.notes.join(" · ")}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Batches history */}
      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <Mail size={16} className="text-slate-600" />
          <div className="font-cabinet text-lg font-bold text-slate-900">Historial de emails procesados</div>
        </div>
        {loading && <div className="px-5 py-10 text-center text-slate-400 text-sm">Cargando...</div>}
        {!loading && batches.length === 0 && (
          <div className="px-5 py-10 text-center text-slate-400 text-sm">
            Aún no has procesado ningún email. Sube un PDF o pega el texto arriba.
          </div>
        )}
        {!loading && batches.map((b) => {
          const isOpen = expanded[b.id];
          const detail = batchDetails[b.id];
          return (
            <div key={b.id} className="border-t border-slate-100" data-testid={`batch-row-${b.id}`}>
              <button
                type="button"
                onClick={() => toggleBatch(b.id)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors text-left"
              >
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <FileText size={14} className="text-slate-500" />
                <div className="flex-1">
                  <div className="font-mono text-sm text-slate-900">
                    {b.source_date || "(sin fecha)"} · {b.source_subject}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {b.created_at?.slice(0, 16).replace("T", " ")}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="bg-white">{b.total} nombres</Badge>
                  {b.invoiced > 0 && <span className="text-emerald-700 font-semibold">{b.invoiced} ✓</span>}
                  {b.pending > 0 && <span className="text-amber-700 font-semibold">{b.pending} ⏳</span>}
                  {b.applied > 0 && <span className="text-slate-700 font-semibold">{b.applied} ✔CRM</span>}
                  {b.discrepancy > 0 && <span className="text-rose-700 font-semibold">{b.discrepancy} ⚠</span>}
                </div>
                {b.invoiced > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); applyToCrm(b.id); }}
                    disabled={applyJob?.batchId === b.id && (applyJob.status === "running" || applyJob.status === "queued")}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                    data-testid={`batch-apply-crm-${b.id}`}
                    title="Aplicar Estado=Terminado en gestion.viajadverdad.com"
                  >
                    {applyJob?.batchId === b.id && (applyJob.status === "running" || applyJob.status === "queued") ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Send size={12} />
                    )}
                    Aplicar CRM
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setToDelete(b.id); }}
                  className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                  data-testid={`batch-delete-${b.id}`}
                  title="Eliminar batch"
                >
                  <Trash2 size={14} />
                </button>
              </button>
              {isOpen && detail && (
                <NamesTable
                  names={detail.names}
                  batchId={b.id}
                  onForceApply={(n) => handleForceApply(b.id, n)}
                  onIgnore={(n) => handleIgnore(b.id, n)}
                  onUnignore={(n) => handleUnignore(b.id, n)}
                  onMoveToPending={(n) => handleMoveToPending(b.id, n)}
                />
              )}
            </div>
          );
        })}
      </section>

      {/* Paste-text dialog */}
      <AlertDialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <AlertDialogContent className="max-w-2xl" data-testid="paste-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pegar contenido del email</AlertDialogTitle>
            <AlertDialogDescription>
              Pega aquí el cuerpo del email de Susana (un nombre por línea).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Anne Sullivan&#10;Amanda King&#10;Alice Wilson..."
            rows={12}
            className="font-mono text-sm"
            data-testid="paste-textarea"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={parsing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handlePasteParse} disabled={parsing} data-testid="paste-parse-btn">
              {parsing ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
              Procesar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este batch?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrarán todas las líneas y referencias del email. Las facturas y trips originales no se ven afectados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBatch} className="bg-rose-600 hover:bg-rose-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KpiCard({ label, value, color = "slate", testid }) {
  const palette = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-5 py-4" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className={`font-cabinet text-3xl font-bold mt-1 ${palette[color] || palette.slate}`}>
        {value}
      </div>
    </div>
  );
}

function NamesTable({ names, batchId, onForceApply, onIgnore, onUnignore, onMoveToPending }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-4 py-2 font-semibold text-slate-700 text-[11px] uppercase tracking-wider">Nombre detectado</th>
            <th className="text-left px-4 py-2 font-semibold text-slate-700 text-[11px] uppercase tracking-wider">Notas</th>
            <th className="text-left px-4 py-2 font-semibold text-slate-700 text-[11px] uppercase tracking-wider">Factura</th>
            <th className="text-left px-4 py-2 font-semibold text-slate-700 text-[11px] uppercase tracking-wider">Estado</th>
            <th className="text-right px-4 py-2 font-semibold text-slate-700 text-[11px] uppercase tracking-wider">Acción</th>
          </tr>
        </thead>
        <tbody>
          {names.map((n, i) => (
            <tr
              key={`${n.idx}-${i}`}
              className={`border-b border-slate-100 ${n.status === "discrepancy" ? "bg-rose-50/40" : ""}`}
              data-testid={`name-row-${n.clean_name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <td className="px-4 py-2 font-medium text-slate-900">
                {n.clean_name}
                {n.is_facturado_flag && (
                  <Badge variant="outline" className="ml-2 text-[9px] bg-slate-900 text-white border-slate-900">_facturado</Badge>
                )}
              </td>
              <td className="px-4 py-2 text-[11px] text-slate-500 max-w-md">
                {(n.notes || []).join(" · ") || "—"}
                {n.status === "ignored" && n.ignore_reason && (
                  <div className="mt-1 italic text-slate-400">Motivo: {n.ignore_reason}</div>
                )}
              </td>
              <td className="px-4 py-2 text-slate-700">
                {n.invoice_number ? (
                  <Link to={`/factura/${n.invoice_id}`} className="font-mono text-slate-900 hover:underline">
                    {n.invoice_number}
                  </Link>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
                {n.matched_client_name && n.matched_client_name !== n.clean_name && (
                  <div className="text-[10px] text-slate-400">{n.matched_client_name}</div>
                )}
              </td>
              <td className="px-4 py-2">
                <StatusBadge status={n.status} />
              </td>
              <td className="px-4 py-2 text-right">
                {n.status === "discrepancy" && onForceApply && (
                  <div className="inline-flex gap-1">
                    <button
                      type="button"
                      onClick={() => onForceApply(n)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-slate-900 text-white hover:bg-slate-800"
                      title="Buscar en el CRM por nombre y aplicar Estado=Terminado"
                      data-testid={`force-apply-${n.idx}`}
                    >
                      <Send size={10} /> Aplicar al CRM
                    </button>
                    {onMoveToPending && (
                      <button
                        type="button"
                        onClick={() => onMoveToPending(n)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100"
                        title="Pasar a 'Esperando' — se aplicará automáticamente cuando se emita la factura final que lo incluya"
                        data-testid={`move-to-pending-${n.idx}`}
                      >
                        <Clock size={10} /> Esperar factura
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onIgnore(n)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
                      title="Marcar como ignorado — no aparecerá más como pendiente"
                      data-testid={`ignore-${n.idx}`}
                    >
                      <Ban size={10} /> Ignorar
                    </button>
                  </div>
                )}
                {n.status === "ignored" && onUnignore && (
                  <button
                    type="button"
                    onClick={() => onUnignore(n)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold text-slate-600 hover:bg-slate-100"
                    title="Quitar la marca de ignorado"
                    data-testid={`unignore-${n.idx}`}
                  >
                    <Undo2 size={10} /> Restaurar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
